import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DATE_FORMAT } from '../../helpers/date-helpers';
import dayjs from 'dayjs';
import { EventApiParameters, apiSlice } from '../api/api';
import { DateRange, FilterAppliedState, FilterState, FilterType } from '../../helpers/common-types';
import { getStateFromQueryString } from '../../app/query-string';

interface CalendarState {
    /*
     * Today's date
     */
    todaysDate: string,
    /*
     * Date currently scrolled to/being viewed
     */
    currentDate: string,
    /*
     * Date range currently being displayed
     */
    currentRange: DateRange,
    currentAppliedFilters: FilterState,
    appendMonths: boolean,
    eventsAreLoading: boolean,
    eventsHaveLoaded: boolean,
    filterBarHeight: number
}
const today = dayjs().format(DATE_FORMAT);

/*-- placeholder for development
const today = "09-01-2025";
--*/

const blankFilterState: FilterState = {
    [FilterType.LOCATION]: { value: [""] },
    [FilterType.CATEGORIES]: { value: [""] },
    [FilterType.NAME]: { value: [""] }
};

const queryStringState = getStateFromQueryString();
let presetRange = { startDate: today, endDate: dayjs(today).endOf('month').format(DATE_FORMAT) };
if (!!queryStringState?.currentDate) {
    presetRange.startDate = dayjs(queryStringState.currentDate).startOf('month').format(DATE_FORMAT);
    presetRange.endDate = dayjs(queryStringState.currentDate).endOf('month').format(DATE_FORMAT);
}

const initialState: CalendarState = {
    todaysDate: today,
    currentDate: queryStringState?.currentDate || today,
    currentRange: presetRange,
    currentAppliedFilters: queryStringState?.currentAppliedFilters || blankFilterState,
    appendMonths: false,
    eventsAreLoading: false,
    eventsHaveLoaded: false,
    filterBarHeight: 0
};

const calendarSlice = createSlice({
    name: 'calendar',
    initialState,
    reducers: {
        /*
         * "currentDate" is the date currently being viewed, and updates with navigation (and scroll)
         */
        setCurrentDate(state, action: PayloadAction<{ newDate: string, shouldChangeRange: boolean }>) {
            const { newDate, shouldChangeRange } = action.payload;
            state.currentDate = newDate;
            if (shouldChangeRange) {
                if (!state.appendMonths) {
                    state.currentRange = getSingleMonthRange(newDate);
                }
                else {
                    state.currentRange = { startDate: today, endDate: dayjs(newDate).endOf('month').format(DATE_FORMAT) }
                }
            }
        },
        /*
         * Payload is whether to set the range to a single month
         */
        resetCurrentDate(state, action: PayloadAction<boolean>) {
            state.currentDate = state.todaysDate;
            if (action?.payload) {
                state.currentRange = getSingleMonthRange(state.todaysDate);
            }
        },
        /*
         * Extends the current range of the calendar without moving the start date (for infinite scroll-style)
         */
        appendMonth(state) {
            const nextMonth = dayjs(state.currentRange.endDate).add(1, 'day').endOf('month');
            state.currentRange = { startDate: state.currentRange.startDate, endDate: nextMonth.format(DATE_FORMAT) };
            state.currentDate = nextMonth.startOf('month').format(DATE_FORMAT);
        },
        /*
         * Shifts the current range forward by a month
         */
        goToNextMonth(state) {
            const currentMonth = dayjs(state.currentDate).month();
            const nextMonth = dayjs(state.currentDate).month(currentMonth + 1).startOf('month');
            state.currentRange = getSingleMonthRange(nextMonth);
            state.currentDate = nextMonth.format(DATE_FORMAT);
        },
        /*
         * Shifts the current range backwards by a month
         */
        goToPreviousMonth(state) {
            const currentMonth = dayjs(state.currentDate).month();
            const prevMonth = dayjs(state.currentDate).month(currentMonth - 1).startOf('month');
            state.currentRange = getSingleMonthRange(prevMonth);
            state.currentDate = prevMonth.format(DATE_FORMAT);
        },
        /*
         * Set new filter state. Filter values are an array in case you want to change controls to checkboxes/additive filters
         */
        updateFilter(state, action: PayloadAction<{ filterType: FilterType, newState: FilterAppliedState }>) {
            const { filterType, newState } = action.payload;
            state.currentAppliedFilters[filterType] = newState;
            const earliestMatchingDate = getEarliestFirstOccurrence(state.currentAppliedFilters);
            const newDate = !!earliestMatchingDate ? dayjs(earliestMatchingDate) : null;
            if (newDate) {
                if (!state.appendMonths) {
                    state.currentRange = getSingleMonthRange(newDate);
                }
                else {
                    if (dayjs(state.currentRange.endDate).isBefore(newDate)) {
                        state.currentRange.endDate = newDate.endOf('month').format(DATE_FORMAT);
                    }
                }
                state.currentDate = newDate.format(DATE_FORMAT);
            }
        },
        /*
         * Reset to blank filter state
         */
        resetFilters(state) {
            state.currentAppliedFilters = blankFilterState;
        },
        /*
         * Set all functionality to append months in a single list
         */
        setAppendMonths(state) {
            state.appendMonths = true;
            state.currentRange.startDate = today;
        },
        /*
         * Set all functionality to view one month at a time, paginated
         */
        setSingleMonth(state) {
            state.appendMonths = false;
            if (!dayjs(state.currentRange.startDate).isSame(state.currentRange.endDate, 'month')) {
                state.currentRange = getSingleMonthRange(state.currentDate);
            }
        },
        /*
         * Controls the offset for scrolling to dates, and sets --filter-bar-height css var for use in css
         */
        setFilterBarHeight(state, action: PayloadAction<number>) {
            state.filterBarHeight = action.payload;
        }
    },
    extraReducers: (builder) => {
        /*
         * Global state for monitoring when data is loading or fetching
         */
        builder.addMatcher(
            apiSlice.endpoints.getEvents.matchPending,
            (state, { payload }) => {
                state.eventsAreLoading = true;
                state.eventsHaveLoaded = false;
            }
        ),
            /*
             * Global state for when data is loaded
             */
            builder.addMatcher(
                apiSlice.endpoints.getEvents.matchFulfilled,
                (state, { payload }) => {
                    state.eventsAreLoading = false;
                    state.eventsHaveLoaded = true;
                }
            )
    }
});

const getEarliestFirstOccurrence = (currentAppliedFilters: FilterState) => {
    let appliedFilters: FilterAppliedState[] = [];
    for (const filterType in currentAppliedFilters) {
        appliedFilters.push(currentAppliedFilters[filterType as FilterType]);
    }
    const earliest = appliedFilters.reduce<string>((date, next: FilterAppliedState) => {
        if (!next.firstOccurance) return date;
        if (!date) return next.firstOccurance;
        return dayjs(next.firstOccurance).isBefore(date) ? next.firstOccurance : date;
    }, '');
    return earliest;
}

const getSingleMonthRange = (date: string | dayjs.Dayjs) => {
    if (typeof (date) === 'string') {
        date = dayjs(date);
    }
    let monthStart = date.startOf('month').startOf('day');
    if (dayjs().isAfter(monthStart)) {
        monthStart = dayjs();
    }
    const monthEnd = date.endOf('month').endOf('day');
    return { startDate: monthStart.format(DATE_FORMAT), endDate: monthEnd.format(DATE_FORMAT) } as EventApiParameters;
}

export const { setCurrentDate, resetCurrentDate, appendMonth, goToNextMonth, goToPreviousMonth, updateFilter, resetFilters, setAppendMonths, setSingleMonth, setFilterBarHeight } = calendarSlice.actions;
export default calendarSlice.reducer;